import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../utils/api';

// API temel URL'i
const API_URL = process.env.REACT_APP_API_URL || 'https://api.345.tr';

export interface AppointmentSectionProps {
  salonInfo: {
    name: string;
    phone: string;
    _id?: string;
  };
  bookingEnabled: boolean;
}

const AppointmentSection: React.FC<AppointmentSectionProps> = ({ salonInfo, bookingEnabled }) => {
  const [customerPhoto, setCustomerPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const { slug } = useParams<{ slug: string }>();
  
  useEffect(() => {
    const fetchCustomerPhoto = async () => {
      try {
        if (salonInfo._id) {
          setLoading(true);
          const response = await apiService.getRandomCustomerPhoto(salonInfo._id);
          console.log('Müşteri fotoğrafı yanıtı:', response);
          
          if (response.success && response.photo && response.photo.photoUrl) {
            // API'den dönen URL'i düzelt - başında / varsa kaldır
            let photoUrl = response.photo.photoUrl;
            if (photoUrl.startsWith('/')) {
              photoUrl = photoUrl.substring(1);
            }
            
            // Tam URL oluştur
            const fullPhotoUrl = `${API_URL}/${photoUrl}`;
            console.log('Oluşturulan fotoğraf URL:', fullPhotoUrl);
            
            setCustomerPhoto(fullPhotoUrl);
          } else {
            setError(true);
            console.warn('Geçerli bir fotoğraf URL\'i bulunamadı:', response);
          }
        }
      } catch (error) {
        console.error('Müşteri fotoğrafı yüklenirken hata:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCustomerPhoto();
  }, [salonInfo._id]);
  
  return (
    <AppointmentSectionContainer>
      <div className="container">
        <AppointmentBox>
          <AppointmentContent>
            <SectionTitle>Online Randevu Alın</SectionTitle>
            <SectionText>
              Birkaç kolay adımda istediğiniz hizmet için randevu oluşturun. Zamanınızı değerli biliyoruz.
            </SectionText>
            <AppointmentSteps>
              <AppointmentStep>
                <StepCircle>1</StepCircle>
                <StepInfo>
                  <StepTitle>Hizmet Seçimi</StepTitle>
                  <StepDescription>Size uygun hizmeti seçin</StepDescription>
                </StepInfo>
              </AppointmentStep>
              <AppointmentStep>
                <StepCircle>2</StepCircle>
                <StepInfo>
                  <StepTitle>Tarih ve Saat</StepTitle>
                  <StepDescription>Size uygun zamanı belirleyin</StepDescription>
                </StepInfo>
              </AppointmentStep>
              <AppointmentStep>
                <StepCircle>3</StepCircle>
                <StepInfo>
                  <StepTitle>Bilgileriniz</StepTitle>
                  <StepDescription>İletişim bilgilerinizi girin</StepDescription>
                </StepInfo>
              </AppointmentStep>
              <AppointmentStep>
                <StepCircle>4</StepCircle>
                <StepInfo>
                  <StepTitle>Onaylama</StepTitle>
                  <StepDescription>Randevunuzu onaylayın</StepDescription>
                </StepInfo>
              </AppointmentStep>
            </AppointmentSteps>
            <ButtonContainer>
              <AppointmentButton to={`/${slug}/randevu`}>
                Hemen Randevu Al <ArrowIcon className="material-icons">arrow_forward</ArrowIcon>
              </AppointmentButton>
            </ButtonContainer>
          </AppointmentContent>
          <AppointmentImageContainer>
            {!loading && customerPhoto ? (
              <AppointmentImage src={customerPhoto} alt="Müşteri fotoğrafı" onError={() => setError(true)} />
            ) : (
              <AppointmentImage src="/images/salon2.jpg" alt="Salon appointment" />
            )}
            <ImageOverlay />
          </AppointmentImageContainer>
        </AppointmentBox>
      </div>
    </AppointmentSectionContainer>
  );
};

const AppointmentSectionContainer = styled.section`
  padding: 100px 0;
  background-color: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}22, ${({ theme }) => theme.colors.secondary}22);
    z-index: 0;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -100px;
    left: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.secondary}22, ${({ theme }) => theme.colors.primary}22);
    z-index: 0;
  }
`;

const AppointmentBox = styled.div`
  display: flex;
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.large};
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column-reverse;
  }
`;

const AppointmentContent = styled.div`
  flex: 1;
  padding: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 40px 30px;
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: 25px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin-top: 15px;
    border-radius: 2px;
  }
`;

const SectionText = styled.p`
  color: ${({ theme }) => theme.colors.lightText};
  line-height: 1.8;
  margin-bottom: 40px;
  font-size: 1.15rem;
  font-family: 'Poppins', sans-serif;
`;

const AppointmentSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const AppointmentStep = styled.div`
  display: flex;
  align-items: flex-start;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StepCircle = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  border-radius: 50%;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 8px 15px rgba(0, 157, 255, 0.2);
`;

const StepInfo = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  margin: 0 0 5px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  font-size: 1.1rem;
  font-family: 'Poppins', sans-serif;
`;

const StepDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const AppointmentButton = styled(Link)`
  display: flex;
  align-items: center;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  padding: 16px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s;
  border: none;
  box-shadow: 0 10px 20px rgba(0, 157, 255, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 25px rgba(0, 157, 255, 0.4);
  }
`;

const ArrowIcon = styled.span`
  margin-left: 8px;
  font-size: 20px;
  transition: transform 0.2s ease;
  
  ${AppointmentButton}:hover & {
    transform: translateX(5px);
  }
`;

const AppointmentImageContainer = styled.div`
  flex: 1;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const AppointmentImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 500px;
  object-fit: cover;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 350px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4));
`;

export default AppointmentSection; 